import { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ShieldAlert, AlertCircle } from 'lucide-react';
import apiReq from 'core/api';
import {
  listUserActions,
  getAuditCategories,
  getAuditActions,
  type AuditFilters,
  type UserActionLogItem,
} from 'services/auditApi';
import FiltersBar, { type UserOption } from './FiltersBar';
import LogTable from './LogTable';
import PayloadDrawer from './PayloadDrawer';

const DEFAULT_LIMIT = 50;

interface ApiErrorLike {
  status?: number;
}

function getStatus(err: unknown): number | undefined {
  return (err as ApiErrorLike)?.status;
}

export default function AuditTab() {
  const [filters, setFilters] = useState<AuditFilters>({ limit: DEFAULT_LIMIT, offset: 0 });
  const [items, setItems] = useState<UserActionLogItem[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<UserActionLogItem | null>(null);

  // Справочники для фильтров
  const [categories, setCategories] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);

  // Справочники категорий/пользователей — однократно при монтировании.
  useEffect(() => {
    getAuditCategories()
      .then((c) => setCategories(c ?? []))
      .catch(() => undefined);

    apiReq<unknown>('user/all')
      .then((res) => {
        const arr = Array.isArray(res)
          ? (res as Array<Record<string, unknown>>)
          : ((res as { users?: unknown[]; items?: unknown[] })?.users ??
            (res as { items?: unknown[] })?.items ??
            []);
        setUsers(
          (arr as Array<Record<string, unknown>>).map((u) => ({
            id: String(u.id ?? ''),
            email: String(u.email ?? ''),
            name: String(u.name ?? ''),
          }))
        );
      })
      .catch(() => undefined);
  }, []);

  // Список действий — при монтировании и при смене категории.
  useEffect(() => {
    getAuditActions(filters.category)
      .then((a) => setActions(a ?? []))
      .catch(() => setActions([]));
  }, [filters.category]);

  // Загрузка журнала при любом изменении фильтров/пагинации.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    listUserActions(filters)
      .then((res) => {
        if (cancelled) return;
        setItems(res?.items ?? []);
        setCount(res?.count ?? 0);
        setForbidden(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setCount(0);
        if (getStatus(err) === 403) {
          setForbidden(true);
        } else {
          setError(err instanceof Error ? err.message : 'Ошибка загрузки журнала');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  // Изменение фильтра → сброс на первую страницу.
  const handleFilterChange = useCallback((patch: Partial<AuditFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch, offset: 0 }));
  }, []);

  // Смена категории сбрасывает выбранное действие.
  const handleCategoryChange = useCallback((category: string | undefined) => {
    setFilters((prev) => ({ ...prev, category, action: undefined, offset: 0 }));
  }, []);

  const handlePage = useCallback((offset: number) => {
    setFilters((prev) => ({ ...prev, offset }));
  }, []);

  const handleLimit = useCallback((limit: number) => {
    setFilters((prev) => ({ ...prev, limit, offset: 0 }));
  }, []);

  const handleReset = useCallback(() => {
    setFilters({ limit: DEFAULT_LIMIT, offset: 0 });
  }, []);

  if (forbidden) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          py: 8,
          textAlign: 'center',
        }}
      >
        <ShieldAlert size={36} color="var(--color-error)" />
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>
          Нет доступа к журналу
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', maxWidth: 360 }}>
          У вашей учётной записи недостаточно прав для просмотра журнала действий пользователей.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <FiltersBar
        filters={filters}
        categories={categories}
        actions={actions}
        users={users}
        onChange={handleFilterChange}
        onCategoryChange={handleCategoryChange}
        onReset={handleReset}
      />

      {error && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 1.25,
            mb: 2,
            borderRadius: 'var(--radius-md)',
            bgcolor: 'var(--color-error-tint)',
            border: '1px solid rgba(220,38,38,0.18)',
          }}
        >
          <AlertCircle size={15} color="var(--color-error)" />
          <Typography sx={{ fontSize: 12.5, color: 'var(--color-error)' }}>{error}</Typography>
        </Box>
      )}

      <LogTable
        items={items}
        count={count}
        loading={loading}
        limit={filters.limit ?? DEFAULT_LIMIT}
        offset={filters.offset ?? 0}
        onPage={handlePage}
        onLimit={handleLimit}
        onRowClick={setSelected}
      />

      {selected && <PayloadDrawer item={selected} onClose={() => setSelected(null)} />}
    </Box>
  );
}
